// Path: src/components/aols/privacy/OLSLMprivacy.tsx
// Updated to fix the double footer issue by removing the local Footer instance.
// Removed the redundant FullHeightLayout wrapper for better integration with parent layouts.
// Provided complete file content as requested.
// 06/07/25 10:22 AM +07

import React, { useCallback, Fragment } from "react";
import styled from "styled-components";
import { Typography } from "antd";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import logo from "/logo.jpg";
// REMOVED: Footer import is no longer needed in this component.
// REMOVED: Unused Layout import from 'antd'.
import { HomeOutlined } from "@ant-design/icons";

const { Title, Paragraph } = Typography;

// Define props interface
interface OLSLMprivacyProps {}

// Styled components
// REMOVED: FullHeightLayout is no longer needed.

const BreadcrumbWrapper = styled.div`
  position: absolute;
  top: 10px;
  left: 20px;
  z-index: 1001;

  .ant-breadcrumb a {
    color: #ffcc00;
    transition: color 0.3s ease;
  }

  .ant-breadcrumb a:hover {
    color: #ffaa00;
  }

  @media (max-width: 768px) {
    left: 10px;
    top: 5px;
    .ant-breadcrumb {
      font-size: 12px;
    }
  }
`;

const CustomBreadcrumb = styled.div`
  margin-bottom: 20px;
  text-align: left;
  font-size: 14px;
  line-height: 1.5714285714285714;

  .breadcrumb-item {
    display: inline-block;
  }

  .breadcrumb-link {
    color: #ffaa00 !important; /* Sunlight orange for active links */
    text-decoration: none;
  }

  .breadcrumb-link:hover {
    color: #fce248 !important; /* Sunlight yellow on hover */
  }

  .breadcrumb-separator {
    color: #fff; /* White separator for contrast */
    margin: 0 8px;
  }

  .breadcrumb-non-active {
    color: #fff !important; /* White for non-active items */
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px;
  background: #111;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  color: #fff;

  @media (max-width: 768px) {
    padding: 20px;
    margin: 10px;
  }
`;

const LogoImg = styled.img`
  width: 150px;
  height: auto;
  margin-bottom: 30px;
  cursor: pointer;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(255, 204, 0, 0.3);
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    width: 120px;
  }
`;

const MainTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 0;
  margin-bottom: 20px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 5px;
  }
`;

interface StyledLinkProps {
  $isButton?: boolean;
}

const MainTitleButton = styled(Link)<StyledLinkProps>`
  background: #ffaa00;
  color: #0427f4;
  font-size: 24px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 6px;
  text-decoration: none;
  transition: background 0.3s ease, transform 0.3s ease;

  &:hover {
    background: #ffcc00;
    color: #0427f4;
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    font-size: 18px;
    padding: 6px 12px;
  }
`;

const LinkStyled = styled.a`
  color: #ffaa00;
  text-decoration: none;
  transition: color 0.3s ease;

  &:hover {
    color: #ffcc00;
    text-decoration: underline;
  }

  &:focus {
    outline: 2px solid #ffcc00;
    outline-offset: 2px;
  }
`;

const MainTitleText = styled(Title)`
  color: #ffffff !important;
  margin: 0 !important;
  font-size: 24px;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const SectionTitle = styled(Title)`
  color: #ffffff !important;
  margin-top: 30px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const SubsectionTitle = styled(Title)`
  color: #ffffff !important;
  margin-top: 20px;
  margin-bottom: 15px;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const PolicyText = styled(Paragraph)`
  line-height: 1.8;
  color: #ddd;
  margin-bottom: 15px;
  white-space: pre-wrap;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const List = styled.ul`
  margin-left: 20px;
  list-style-type: disc;

  li {
    margin: 5px 0;
    color: #ddd;

    @media (max-width: 768px) {
      font-size: 0.9rem;
    }
  }
`;

const OLSLMprivacy: React.FC<OLSLMprivacyProps> = () => {
  const handleLogoClick = useCallback(() => {
    window.location.href = "https://www.olsme.com";
  }, []);

  return (
    // UPDATED: Using React.Fragment instead of FullHeightLayout
    <Fragment>
      <Helmet>
        <title>OLSLM Assets Privacy Policy - OneLightSystem OLS</title>
        <meta
          name="description"
          content="Read the OLSLM Assets Privacy Policy for OneLightSystem OLS. Learn about the management, distribution, usage, and security of OLSLM assets in our blockchain-based ecosystem."
        />
        <meta
          name="keywords"
          content="OLSLM Assets, Privacy Policy, Sun Light Meditation, OLS Education, Light Civilization, OneLightSystem OLS, blockchain security"
        />
        <meta name="author" content="OneLightSystem OLS" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="OLSLM Assets Privacy Policy - OneLightSystem OLS" />
        <meta
          property="og:description"
          content="Explore the OLSLM Assets Privacy Policy for OneLightSystem OLS, detailing the management, distribution, usage, and security of OLSLM assets."
        />
        <meta property="og:url" content="https://www.olsme.com/OLSLMprivacy" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.olsme.com/logo.jpg" />
        <meta property="og:image:alt" content="OneLightSystem OLS Logo" />
        <meta property="og:site_name" content="OneLightSystem OLS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="OLSLM Assets Privacy Policy - OneLightSystem OLS" />
        <meta
          name="twitter:description"
          content="Learn about the OLSLM Assets Privacy Policy for OneLightSystem OLS, including management, distribution, and security of OLSLM assets."
        />
        <meta name="twitter:image" content="https://www.olsme.com/logo.jpg" />
        <meta name="twitter:image:alt" content="OneLightSystem OLS Logo" />
        <meta name="twitter:site" content="@onelightsystem" />
        <meta name="twitter:creator" content="@onelightsystem" />
        <link rel="canonical" href="https://www.olsme.com/OLSLMprivacy" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "OLSLM Assets Privacy Policy - OneLightSystem OLS",
            description:
              "OLSLM Assets Privacy Policy for OneLightSystem OLS, detailing the management, distribution, usage, and security of OLSLM assets in a blockchain-based ecosystem.",
            url: "https://www.olsme.com/OLSLMprivacy",
            publisher: {
              "@type": "Organization",
              name: "OneLightSystem OLS",
              logo: "https://www.olsme.com/logo.jpg",
              contactPoint: {
                "@type": "ContactPoint",
                email: "info@olsme.com",
                contactType: "Customer Service",
              },
              address: {
                "@type": "PostalAddress",
                streetAddress: "2033 Ralston Ave", 
                addressLocality: "Belmont",
                addressRegion: "CA",
                postalCode: "94002", 
                addressCountry: "US",             
              },
              founder: {
                "@type": "Person",
                name: "Nazar Pankiv",
                jobTitle: "CEO",
              },
              sameAs: [
                "https://www.olsme.com",
                "https://www.ols-med.net",
                "https://twitter.com/onelightsystem",
                "https://www.facebook.com/onelightsystemols",
                "https://www.linkedin.com/company/one-light-system",
                "https://www.youtube.com/@ONELIGHTSYSTEMOLSMeditation",
              ],
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: "https://www.olsme.com",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "OLSLM Assets Privacy",
                  item: "https://www.olsme.com/OLSLMprivacy",
                },
              ],
            },
          })}
        </script>
      </Helmet>
      <BreadcrumbWrapper>
        <CustomBreadcrumb>
          <span className="breadcrumb-item">
            <Link to="/" className="breadcrumb-link">
              <HomeOutlined /> Home
            </Link>
          </span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-item">
            <Link to="/privacy" className="breadcrumb-link">
              Main Privacy
            </Link>
          </span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-item breadcrumb-non-active">
            OLSLM Assets Privacy
          </span>
        </CustomBreadcrumb>
      </BreadcrumbWrapper>
      <ContentWrapper>
        <LinkStyled href="https://www.olsme.com" aria-label="Go to OneLightSystem OLS homepage">
          <LogoImg src={logo} alt="OneLightSystem OLS Logo" onClick={handleLogoClick} />
        </LinkStyled>
        <MainTitleWrapper>
          <MainTitleButton to="/aOLSLMasset" aria-label="Go to OLSLM Assets page">
            OLSLM Assets
          </MainTitleButton>
          <MainTitleText level={1}>
            Privacy Disclosure
          </MainTitleText>
        </MainTitleWrapper>

        <PolicyText>
          <strong>OneLightSystem OLS</strong>
          <br />
          OLSLM Assets Policy for OneLightSystem OLS
          <br />
          Versions: 2800.1759.1639.1364.1295.1259.1153.962.934.906.786
          <br />
          Last Updated: April 08, 2025
          <br />
          © OneLightSystem OLS, LLC | OLS iee.aeo
          <br />
          CEO: Nazar Pankiv
        </PolicyText>

        <SectionTitle level={2}>
          Introduction
        </SectionTitle>
        <PolicyText>
          Welcome to the OLSLM Assets Policy for OneLightSystem OLS (hereinafter referred to as "OLS"). OLSLM®© (OneLightSystem LightMinute) is a registered trademark and represents a unique individual equity value processed and recognized exclusively on the OLS platform at{" "}
          <LinkStyled href="https://www.olsme.com" aria-label="Visit olsme.com">
            www.olsme.com
          </LinkStyled>. This policy outlines the management, distribution, usage, and security of OLSLM assets, which operate on a limited total supply model within a blockchain-based ecosystem.
        </PolicyText>

        <SectionTitle level={2}>
          1. Definitions
        </SectionTitle>
        <List>
          <li>
            <strong>OLSLM Assets:</strong> Digital assets representing OLS LightMinutes (OLSLM), an equity value unit exclusive to the OLS platform.
          </li>
          <li>
            <strong>OLS User:</strong> Any individual or entity registered on the OLS platform with access to OLSLM assets.
          </li>
          <li>
            <strong>OLS Platform:</strong> The official website (
            <LinkStyled href="https://www.olsme.com" aria-label="Visit olsme.com">
              www.olsme.com
            </LinkStyled>) and associated blockchain infrastructure operated by OneLightSystem OLS, LLC.
          </li>
          <li>
            <strong>OneLightSystem LightMinute (OLSLM):</strong> A proprietary unit of equity value generated/earned, managed, and tradable within the OLS ecosystem.
          </li>
        </List>

        <SectionTitle level={2}>
          2. OLSLM Assets Overview
        </SectionTitle>
        <SubsectionTitle level={3}>
          2.1 Availability
        </SubsectionTitle>
        <List>
          <li>
            <strong>(a.a) available OLSLM assets (available assets):</strong> Assets currently accessible for acquisition, use, or trade within the OLS inner market.
          </li>
          <li>
            <strong>Limited Total Supply:</strong> OLSLM assets are capped, ensuring scarcity and value stability.
          </li>
        </List>

        <SubsectionTitle level={3}>
          2.2 Purpose
        </SubsectionTitle>
        <PolicyText>
          Any OLS user may obtain available OLSLM assets to:
        </PolicyText>
        <List>
          <li>
            <strong>Buy and Use:</strong> Purchase assets for personal utilization or benefits.
          </li>
          <li>
            <strong>Use and Trade:</strong> Utilize assets within the OLS ecosystem and trade them on the inner market.
          </li>
        </List>

        <SectionTitle level={2}>
          3. Distribution and Categories
        </SectionTitle>
        <SubsectionTitle level={3}>
          3.1 Distribution Model
        </SubsectionTitle>
        <PolicyText>
          <strong>Initial Coin Offering (ICO):</strong>
        </PolicyText>
        <List>
          <li>1.8% available OLSL9M assets category 9 executive (352 OLS9LM).</li>
          <li>0.5% available OLSL3M assets category 4 (33 OLS4LM).</li>
          <li>1.45% total available OLSLM assets (385 Entrepreneur Advantage Pioneer INNER VALUE wallet).</li>
        </List>
        <PolicyText>
          <strong>Inner Market:</strong> Assets are distributed via the OLS platform’s transparent blockchain.
        </PolicyText>

        <SubsectionTitle level={3}>
          3.2 Categories
        </SubsectionTitle>
        <List>
          <li>
            <strong>OLSL1-9M:</strong> Value categories ranging from OLSL1M (basic master) to OLS9LM (top-executive), each with unique block encryption validation based on usage conditions.
          </li>
          <li>Obtain to use option</li>
          <li>Obtain to store and trade option (accumulation % available)</li>
        </List>

        <SectionTitle level={2}>
          4. Account Management
        </SectionTitle>
        <SubsectionTitle level={3}>
          4.1 Asset Ownership
        </SubsectionTitle>
        <PolicyText>
          OLS Users can manage OLSLM assets through a free account upon making their first transaction (any category). Ownership is validated via blockchain security with a unique "light body" field transfer and support.
        </PolicyText>

        <SubsectionTitle level={3}>
          4.2 Withdrawals and Deposits
        </SubsectionTitle>
        <List>
          <li>
            <strong>Process:</strong> New OLSLM asset users can withdraw or deposit assets with their evaluation conditions.
          </li>
          <li>
            <strong>Request to obtain for use:</strong> To use outside the OLS marketplace, owners submit a withdrawal/deposit request and receive the validation condition certificate:
          </li>
          <ul style={{ marginLeft: "20px", listStyleType: "circle" }}>
            <li>Categories definance (OLSL1-9M).</li>
            <li>Time periods with weights in</li>
            <li>Valid transfer certificate.</li>
          </ul>
          <li>
            <strong>Free Account Eligibility:</strong> Requires at least one transaction within 9 days.
          </li>
        </List>

        <SectionTitle level={2}>
          5. Transfer Process
        </SectionTitle>
        <SubsectionTitle level={3}>
          5.1 Levels of Transfer
        </SubsectionTitle>
        <List>
          <li>
            <strong>OLSL4M:</strong> Advanced transfer.
          </li>
          <li>
            <strong>OLS9LM:</strong> Executive transfer
          </li>
        </List>

        <SubsectionTitle level={3}>
          5.2 Steps
        </SubsectionTitle>
        <List>
          <li>
            <strong>Reserve Amount Value:</strong> Lock the desired OLSLM amount.
          </li>
          <li>
            <strong>Select:</strong> Usage assets or Storage (Holdings):
          </li>
          <li>
            <strong>Review Agreements:</strong> Accept terms and conditions.
          </li>
          <li>
            <strong>Receive Personal Crafted LightBody Certificate:</strong> Obtain the usage or storage certificate manual.
          </li>
          <li>
            <strong>Personal Support:</strong> Access Assets Management Support
          </li>
        </List>

        <SubsectionTitle level={3}>
          5.3 Benefits
        </SubsectionTitle>
        <PolicyText>
          <strong>Overall Usage Receiver Benefits:</strong> Enhanced real value assets with a "light body" field extra layer formation.
        </PolicyText>

        <SectionTitle level={2}>
          6. Usage and Recalibration
        </SectionTitle>
        <SubsectionTitle level={3}>
          6.1 Assets Usage
        </SubsectionTitle>
        <List>
          <li>
            <strong>Minimum Amount:</strong> Minimum OLSLM required for utilization as usage assets.
          </li>
          <li>
            <strong>Manual:</strong> Follow the OLSLM Light Body Field Usage Manual.
          </li>
        </List>

        <SubsectionTitle level={3}>
          6.2 Recalibration Fees
        </SubsectionTitle>
        <List>
          <li>
            <strong>Non-OLS Student:</strong> 10% annual recalibration fee if not an active OLS student.
          </li>
          <li>
            <strong>OLSME User:</strong> 50% subscription discount recalibration fee for ongoing OLSME benefits.
          </li>
        </List>

        <SectionTitle level={2}>
          7. Blockchain Security and Transparency
        </SectionTitle>
        <List>
          <li>
            <strong>Validation:</strong> Each OLSLM asset uses unique block encryption for secure trading and ownership verification.
          </li>
          <li>
            <strong>Transparency:</strong> The OLS blockchain ensures all transactions are visible in the public search portfolios.
          </li>
          <li>
            <strong>Reports:</strong> OLS data analysis trend reports are available at the OLS Data Center for asset pool trends.
          </li>
        </List>

        <SectionTitle level={2}>
          8. OLSLM Assets Pool
        </SectionTitle>
        <List>
          <li>
            <strong>Available Pool:</strong> The inner market maintains an active pool of OLS1-9LM categories.
          </li>
          <li>
            <strong>Trend Reports:</strong> Accessible via the OLS Data Center for user insights.
          </li>
        </List>

        <SectionTitle level={2}>
          9. Contact and Oversight
        </SectionTitle>
        <List>
          <li>
            <strong>Authority:</strong> OLS iee.aeo oversees asset management and policy updates.
          </li>
          <li>
            <strong>CEO:</strong> Nazar Pankiv, contactable via{" "}
            <LinkStyled href="https://www.olsme.com/support" aria-label="Visit OLS support page">
              www.olsme.com/support
            </LinkStyled>.
          </li>
          <li>
            <strong>Reference:</strong> 2859.OLS iee.aeo
          </li>
        </List>

        <SectionTitle level={2}>
          10. Legal Notices
        </SectionTitle>
        <List>
          <li>
            <strong>Trademark:</strong> OLSLM®© is a registered trademark of OneLightSystem OLS, LLC.
          </li>
          <li>
            <strong>Confidentiality:</strong> All OLSLM data is confidential and protected from unauthorized disclosure.
          </li>
          <li>
            <strong>Updates:</strong> This policy may be revised at any time, with users notified via the OLS platform.
          </li>
        </List>
      </ContentWrapper>
      {/* REMOVED: The <Footer /> component is no longer rendered here. */}
    </Fragment>
  );
};

export default OLSLMprivacy;